/**
 * InkCanvas — freehand drawing layer (finger / stylus / mouse).
 * Strokes are stored normalized (0..1 of the box) so they re-render at any zoom.
 * `baseImage` is a previously saved PNG (data URL) shown underneath new strokes.
 */
import { useEffect, useRef } from "react";

function drawStrokes(ctx, strokes, w, h, scale) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const s of strokes) {
    if (!s.points?.length) continue;
    ctx.globalCompositeOperation = s.erase ? "destination-out" : "source-over"; // eraser rubs out whatever is underneath
    ctx.strokeStyle = s.erase ? "#000" : s.color || "#111827";
    ctx.lineWidth = (s.width || 1.6) * scale;
    ctx.beginPath();
    const [x0, y0] = s.points[0];
    ctx.moveTo(x0 * w, y0 * h);
    if (s.points.length === 1) ctx.lineTo(x0 * w + 0.1, y0 * h + 0.1);
    for (let i = 1; i < s.points.length; i++) {
      const [px, py] = s.points[i - 1];
      const [x, y] = s.points[i];
      ctx.quadraticCurveTo(px * w, py * h, ((px + x) / 2) * w, ((py + y) / 2) * h);
    }
    ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Render strokes (+ base image) to a transparent PNG data URL at `wPts` x `hPts` points, 2x resolution. */
export async function exportInkPng(strokes, baseImage, wPts, hPts) {
  const res = 2;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(wPts * res));
  canvas.height = Math.max(1, Math.round(hPts * res));
  const ctx = canvas.getContext("2d");
  const img = await loadImage(baseImage);
  if (img) ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  drawStrokes(ctx, strokes, canvas.width, canvas.height, res);
  return canvas.toDataURL("image/png");
}

export default function InkCanvas({ width, height, scale = 1, strokes, baseImage, color, penWidth, eraser = false, eraserWidth = 14, active, onStrokeEnd, className = "", style, testId }) {
  const ref = useRef(null);
  const drawing = useRef(null);
  const baseRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadImage(baseImage).then((img) => { if (!cancelled) { baseRef.current = img; paint(); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImage]);

  const paint = (live) => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);
    if (baseRef.current) ctx.drawImage(baseRef.current, 0, 0, width, height);
    drawStrokes(ctx, live ? [...strokes, live] : strokes, width, height, scale);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { paint(); }, [strokes, width, height, scale]);

  const norm = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const down = (e) => {
    if (!active) return;
    e.preventDefault();
    e.stopPropagation();
    ref.current.setPointerCapture?.(e.pointerId);
    drawing.current = eraser ? { erase: true, width: eraserWidth, points: [norm(e)] } : { color, width: penWidth, points: [norm(e)] };
    paint(drawing.current);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current.points.push(norm(e));
    paint(drawing.current);
  };
  const up = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const s = drawing.current;
    drawing.current = null;
    onStrokeEnd?.([...strokes, s]);
  };

  return (
    <canvas
      ref={ref}
      className={className}
      style={{ width, height, touchAction: active ? "none" : "auto", pointerEvents: active ? "auto" : "none", cursor: active ? (eraser ? "cell" : "crosshair") : "default", ...style }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onPointerLeave={(e) => drawing.current && up(e)}
      data-testid={testId}
    />
  );
}
